/* global runtime */
// Redigering, vedlegg, reversering og reiseregninger i lønnsflyten.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const recalculatePayrollRunLine = (employee, line, paymentDate, payrollSettings) => {
  if (!safeArray(line.items).length) return stripUndefinedDeep({
    ...line, items: [], grossSalary: 0, taxDeduction: 0, deductions: 0,
    expenseReimbursement: 0, postedExpenseReimbursement: 0, employerContribution: 0,
    holidayPayAccrual: 0, holidayPayBase: 0, holidayPayPayout: 0, pensionContribution: 0, netPay: 0,
  });
  const items = safeArray(line.items).map((item) => buildPayrollRunItem({ employee, line: item, paymentDate, sourceId: item.sourceId, payrollSettings }));
  const grossSalary = items.reduce((sum, item) => sum + num(item.grossSalary), 0);
  const expenseReimbursement = items.filter((item) => isPayrollReimbursementPayType(item.payType, payrollSettings)).reduce((sum, item) => sum + num(item.amount), 0);
  const taxDeduction = items.reduce((sum, item) => sum + num(item.taxDeduction), 0);
  const deductions = items.reduce((sum, item) => sum + payrollItemDeductionAmount(item, payrollSettings), 0);
  const employerContribution = items.reduce((sum, item) => sum + num(item.employerContributionAmount), 0);
  const holidayPayAccrual = items.reduce((sum, item) => sum + num(item.holidayPayAmount), 0);
  // A manual pension premium replaces the calculated OTP of the line; summing
  // item OTP would count both (mirrors linePensionContribution in the backend).
  const pensionContribution = payrollLinePensionContribution({ items, grossSalary }, payrollSettings);
  const cashPayBase = items
    .filter((item) => item.payout && !isPayrollReimbursementPayType(item.payType, payrollSettings))
    .filter((item) => getPayrollPayTypeDefinition(item.payType, payrollSettings)?.kind !== 'Trekk' && item.payType !== 'pension-premium')
    .reduce((sum, item) => sum + num(item.amount), 0) + items.filter((item) => item.payType === 'holiday-deduction').reduce((sum, item) => sum + num(item.grossSalary), 0);
  return stripUndefinedDeep({
    ...line,
    items,
    grossSalary,
    taxDeduction,
    deductions,
    expenseReimbursement,
    postedExpenseReimbursement: expenseReimbursement,
    employerContribution,
    employerContributionRate: payrollEmployerContributionRateFor(employee, payrollSettings),
    employerContributionZone: payrollSettings.employerContributionZone || employee.employerContributionZone,
    holidayPayAccrual,
    holidayPayBase: items.filter((item) => item.holidayPay).reduce((sum, item) => sum + num(item.grossSalary), 0),
    holidayPayPayout: items.filter((item) => item.payType === 'holiday-pay-current' || item.payType === 'holiday-pay-previous').reduce((sum, item) => sum + num(item.amount), 0),
    pensionContribution,
    // Uten denne beholdt en redigert linje gammel nettolonn — og en ny linje fikk
    // ingen i det hele tatt, slik at 2930 ble kontert med 0.
    netPay: Math.max(0, cashPayBase + expenseReimbursement - taxDeduction - deductions),
  });
};
const updatePayrollRunNote = async (runId, note = '') =>
  updatePayrollRunById(runId, (run) => stripUndefinedDeep({
    ...run,
    note: String(note || '').trim(),
    updatedAt: nowIso(),
  }));

const addPayrollRunLine = async (runId, employeeId, draft = {}) => {
  const state = mergeState(runtime.appState);
  const employee = safeArray(state.employees).find((entry) => entry.id === employeeId);
  if (!employee) throw new Error(t('svc.employeeNotFound'));
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  if (!run) throw new Error(t('svc.payrollRunNotFound'));
  if (run.status !== 'draft') throw new Error(t('pay.payrollRunNotEditableForNewLines'));
  const availablePayTypes = payrollPayTypesForSettings(state.payrollSettings).filter((entry) => entry.active !== false);
  const workflowPayTypes = {
    'holiday-pay': new Set(['holiday-pay-current', 'holiday-pay-previous']),
    pension: new Set(['pension-premium']),
  };
  const allowedByWorkflow = workflowPayTypes[draft.sourceWorkflow]?.has(draft.payType) === true;
  const definition = availablePayTypes.find((entry) => (entry.payType || entry.id) === draft.payType);
  if (!definition || (definition.manualEntry === false && !allowedByWorkflow)) throw new Error(t('pay.payTypeNotManualEntry'));
  const cleanPayType = draft.payType;
  const defaultRules = {
    payout: definition.kind !== 'Trekk' && definition.kind !== 'Naturalytelse',
    taxable: definition.tax !== false,
    employerContribution: definition.aga !== false,
    holidayPay: definition.holiday !== false,
    pension: definition.pension !== false,
  };
  const overrideReason = String(draft.calculationRuleOverrideReason || '').trim();
  const overrideRules = draft.calculationRuleOverride === true;
  if (overrideRules && !overrideReason) throw new Error(t('pay.explainRuleOverrideRequired'));
  const selectedRules = overrideRules ? {
    payout: draft.payout !== false,
    taxable: draft.taxable !== false,
    employerContribution: draft.employerContribution !== false,
    holidayPay: draft.holidayPay !== false,
    pension: draft.pension !== false,
  } : defaultRules;
  const fixedLine = draft.makeFixedLine ? stripUndefinedDeep({
    id: createId('fixed-pay'), employmentLabel: draft.employmentLabel || employee.name || t('pay.employee'),
    payType: cleanPayType, amount: draft.variableAmount ? undefined : num(draft.amount),
    variableAmount: draft.variableAmount === true, ...selectedRules,
    calculationRuleOverride: overrideRules || undefined,
    calculationRuleOverrideReason: overrideRules ? overrideReason : undefined,
    comment: draft.comment || undefined, createdAt: nowIso(),
  }) : null;
    const paymentDate = run.paymentDate || todayIso();
    // The run's withholding mode must survive every recalculation of its lines.
    const runPayrollSettings = { ...(state.payrollSettings || {}), runTaxDeductionMode: normalizePayrollTaxDeductionMode(run.taxDeductionMode) };
    let found = false;
    const item = buildPayrollRunItem({
      employee,
      paymentDate,
      line: stripUndefinedDeep({
        id: createId('payroll-item'),
        label: definition.name,
        payType: cleanPayType,
        quantity: num(draft.quantity, 1),
        rate: num(draft.rate),
        amount: num(draft.amount),
        ...selectedRules,
        calculationRuleOverride: overrideRules || undefined,
        calculationRuleOverrideReason: overrideRules ? overrideReason : undefined,
        sourceWorkflow: draft.sourceWorkflow || undefined,
        comment: draft.comment || '',
        employmentType: draft.employmentType || undefined,
        taxResidenceCountry: draft.taxResidenceCountry || undefined,
        earningCountry: draft.earningCountry || undefined,
        incomeRelation: draft.incomeRelation || undefined,
      }),
      payrollSettings: runPayrollSettings,
    });
    const lines = safeArray(run.lines).map((line) => {
      if (line.employeeId !== employeeId) return line;
      found = true;
        return recalculatePayrollRunLine(employee, { ...line, items: [...safeArray(line.items), item] }, paymentDate, runPayrollSettings);
    });
    if (!found) {
      lines.push(recalculatePayrollRunLine(employee, {
        id: createId('payroll-line'),
        employeeId: employee.id,
        employeeName: employee.name || '-',
        items: [item],
        hours: 0,
        postedExpenseIds: [],
        employerContributionRate: num(employee.employerContributionRate, 14.1),
        employerContributionZone: employee.employerContributionZone,
      }, paymentDate, runPayrollSettings));
    }
  const savedRun = stripUndefinedDeep({ ...run, lines, updatedAt: nowIso() });
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'replace', runId, run: savedRun,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  if (fixedLine) {
    await updateStateAndPersist((current) => ({
      ...current,
      employees: safeArray(current.employees).map((entry) => entry.id === employeeId ? stripUndefinedDeep({
        ...entry, fixedPayrollLines: [...safeArray(entry.fixedPayrollLines), fixedLine], updatedAt: nowIso(),
      }) : entry),
    }));
  } else emitRuntime();
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  return result.run || savedRun;
};
const deletePayrollRunLineItem = async (runId, employeeId, itemId) => {
  const state = mergeState(runtime.appState), employee = safeArray(state.employees).find((entry) => entry.id === employeeId);
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  if (!employee) throw new Error(t('svc.employeeNotFound'));
  if (!run) throw new Error(t('svc.payrollRunNotFound'));
  if (run.status !== 'draft') throw new Error(t('pay.payrollRunNotEditableForNewLines'));
  let removed = false;
  const payrollSettings = { ...(state.payrollSettings || {}), runTaxDeductionMode: normalizePayrollTaxDeductionMode(run.taxDeductionMode) };
  const lines = safeArray(run.lines).map((line) => line.employeeId !== employeeId ? line : recalculatePayrollRunLine(employee, { ...line, items: safeArray(line.items).filter((item) => item.id !== itemId || !(removed = true)) }, run.paymentDate || todayIso(), payrollSettings));
  if (!removed) throw new Error(t('pay.salaryLineNotFound'));
  const savedRun = stripUndefinedDeep({ ...run, lines, updatedAt: nowIso() });
  const result = await authorizedPost('payrollRunMutationHttp', { businessId: runtime.activeBusinessId, action: 'replace', runId, run: savedRun });
  if (result.state) runtime.appState = mergeState(result.state);
  emitRuntime(); return result.run || savedRun;
};

const uploadPayrollRunDocument = async (runId, file, description = '') => {
  if (!runId) throw new Error(t('svc.missingPayrollPayment'));
  if (!(file instanceof File)) throw new Error(t('svc.pickFileFirst'));
  assertAllowedEmployeeDocumentFile(file);
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!runtime.activeBusinessId) throw new Error(t('svc.missingActiveBusiness'));

  const id = createId('payroll-doc');
  const cleanFileName = safeFilePart(file.name || 'lonnsdokument');
  const storagePath = `businesses/${runtime.activeBusinessId}/payrollRuns/${runId}/documents/${Date.now()}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      businessId: runtime.activeBusinessId,
      payrollRunId: runId,
      source: 'plumme',
    },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  const document = stripUndefinedDeep({
    id,
    name: cleanFileName,
    mimeType: file.type || '',
    size: file.size,
    description: String(description || '').trim() || undefined,
    uploadedAt: nowIso(),
    storagePath,
    downloadUrl: downloadUrl || undefined,
  });
  const savedRun = await updatePayrollRunById(runId, (run) => stripUndefinedDeep({
    ...run,
    documents: [...safeArray(run.documents), document],
    updatedAt: nowIso(),
  }));
  return { run: savedRun, document };
};

const attachPayrollRunInboxDocument = async (runId, inboxEntryId) => {
  if (!runId) throw new Error(t('svc.missingPayrollPayment'));
  if (!inboxEntryId) throw new Error(t('svc.pickVoucherFirst'));
  let document = null;
  const savedRun = await updatePayrollRunById(runId, (run, state) => {
    const receiptEntry = safeArray(state.receiptInbox).find((candidate) => candidate.id === inboxEntryId);
    const documentationEntry = safeArray(state.documentationInbox).find((candidate) => candidate.id === inboxEntryId);
    const entry = receiptEntry || documentationEntry;
    const source = receiptEntry ? 'receiptInbox' : 'documentationInbox';
    if (!entry) throw new Error(t('svc.docNotFoundInInbox'));
    const attachment = safeArray(entry.attachments)[0] || {};
    document = stripUndefinedDeep({
      id: createId('payroll-doc'),
      name: entry.name || entry.fileName || attachment.name || (source === 'documentationInbox' ? t('sale.inboxPicker.fallbackDocumentation') : t('pay.receiptInboxFallbackName')),
      mimeType: entry.mimeType || entry.attachmentMimeType || attachment.mimeType || '',
      size: num(entry.size || attachment.size),
      description: entry.supplierName || entry.subject || entry.description || entry.note || undefined,
      uploadedAt: entry.receivedAt || entry.createdAt || nowIso(),
      storagePath: entry.storagePath || entry.attachmentStoragePath || attachment.storagePath || undefined,
      downloadUrl: entry.downloadUrl || entry.file || attachment.downloadUrl || attachment.file || undefined,
      receiptInboxEntryId: source === 'receiptInbox' ? entry.id : undefined,
      documentationInboxEntryId: source === 'documentationInbox' ? entry.id : undefined,
      source,
    });
    return stripUndefinedDeep({
      ...run,
      documents: [...safeArray(run.documents), document],
      updatedAt: nowIso(),
    });
  });
  return { run: savedRun, document };
};

const reversePayrollRun = async (runId) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'reverse', runId,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.run;
};

const deleteReversedPayrollRun = async (runId) => {
  const result = await authorizedPost('payrollRunMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'delete', runId,
  });
  if (result.state) runtime.appState = mergeState(result.state);
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  emitRuntime();
  return result.deletedRunId || runId;
};

const getPayrollRunPostings = (runId) => {
  const state = mergeState(runtime.appState);
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  return safeArray(run?.payrollPostingRows).length ? safeArray(run.payrollPostingRows) : buildPayrollPostingRows(run);
};

const getPayrollRunAccountingVouchers = (runId) => {
  const state = mergeState(runtime.appState);
  const run = safeArray(state.payrollRuns).find((entry) => entry.id === runId);
  return window.PlummePayrollRunAccountingVouchers.fromRun(run);
};

const mergeTravelAttachments = (items) => Array.from(new Map(safeArray(items)
  .filter(Boolean)
  .map((item) => [item.id || item.storagePath || item.name, stripUndefinedDeep(item)]))
  .values());

const uploadTravelExpenseAttachments = async (travelId, line = {}) => {
  const attachments = safeArray(line.attachments);
  const fresh = attachments.filter((attachment) => attachment?.file instanceof File);
  const carried = attachments
    .filter((attachment) => !(attachment?.file instanceof File))
    .map((attachment) => stripUndefinedDeep({
      id: attachment.id || attachment.storagePath || attachment.name || createId('travel-att'),
      name: attachment.name || t('asset.attachmentFallback'),
      mimeType: attachment.mimeType || attachment.type || '',
      storagePath: attachment.storagePath,
      downloadUrl: attachment.downloadUrl,
      size: attachment.size,
      source: attachment.source || 'plumme',
    }));
  if (!fresh.length) return { ...line, attachments: mergeTravelAttachments(carried), hasAttachment: carried.length > 0 || line.hasAttachment === true };
  const uploaded = [];
  for (const attachment of fresh) {
    const cleanFileName = safeFilePart(attachment.name, 'kvittering');
    if (!runtime.storage || !runtime.activeBusinessId) {
      uploaded.push(stripUndefinedDeep({
        id: attachment.id || createId('travel-att'),
        name: cleanFileName,
        mimeType: attachment.type || attachment.file?.type || '',
        size: attachment.size || attachment.file?.size,
        source: 'local-file',
      }));
      continue;
    }
    const storagePath = `businesses/${runtime.activeBusinessId}/travel/${travelId}/expense-lines/${line.id || createId('travel-exp')}/attachments/${Date.now()}-${cleanFileName}`;
    const ref = runtime.storage.ref().child(storagePath);
    await ref.put(attachment.file, { contentType: attachment.type || attachment.file.type || 'application/octet-stream' });
    uploaded.push(stripUndefinedDeep({
      id: attachment.id || createId('travel-att'),
      name: cleanFileName,
      mimeType: attachment.type || attachment.file.type || '',
      size: attachment.size || attachment.file.size,
      storagePath,
      downloadUrl: await ref.getDownloadURL().catch(() => ''),
      source: 'firebase-storage',
    }));
  }
  return { ...line, attachments: mergeTravelAttachments([...carried, ...uploaded]), hasAttachment: true };
};

const prepareEmployeePortalTravelForSave = async (travel = {}) => {
  const id = travel.id || createId('travel');
  const expenseLines = await Promise.all(safeArray(travel.expenseLines ?? travel.expenses)
    .map((line) => uploadTravelExpenseAttachments(id, { ...line, id: line.id || createId('travel-exp') })));
  return stripUndefinedDeep({
    ...travel,
    id,
    ...(expenseLines.length ? { expenseLines } : {}),
  });
};

const updateEmployeePortalTravelStatus = async (travelId, status, options = {}) => {
  if (!travelId) throw new Error(t('svc.missingTravel'));
  const allowed = new Set(['draft', 'submitted', 'approved', 'rejected', 'included-in-payroll']);
  if (!allowed.has(status)) throw new Error(t('svc.invalidTravelStatus'));
  const updatedAt = nowIso();
  const rejectionNote = String(options?.rejectionNote || options?.note || '').trim();
  let savedTravel = null;
  await updateStateAndPersist((state) => ({
    ...state,
    employeePortalTravels: safeArray(state.employeePortalTravels).map((travel) => {
      if (travel.id !== travelId) return travel;
      const [missingNok] = status === 'approved' ? payrollTravelMissingNokLines(travel) : [];
      if (missingNok) throw new Error(t('pay.foreignAmount.missingOnApproval', { description: missingNok.description || missingNok.category || '', currency: payrollExpenseCurrency(missingNok) }));
      savedTravel = stripUndefinedDeep({
        ...travel,
        status,
        updatedAt,
        ...(status === 'rejected' ? { rejectionNote, rejectedAt: updatedAt } : {}),
        ...(status === 'approved' ? { approvedAt: updatedAt, rejectionNote: undefined } : {}),
        changeLog: appendTravelChangeLog(travel.changeLog, travelChangeEntry({
          action: status,
          note: status === 'rejected' ? rejectionNote : '',
        })),
      });
      return savedTravel;
    }),
  }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedTravel;
};

const saveEmployeePortalTravel = async (travel = {}, { change = null } = {}) => {
  const preparedTravel = await prepareEmployeePortalTravelForSave(travel);
  let savedTravel = null;
  await updateStateAndPersist((state) => {
    const existing = preparedTravel.id ? safeArray(state.employeePortalTravels).find((entry) => entry.id === preparedTravel.id) : null;
    savedTravel = normalizeEmployeePortalTravel({
      ...preparedTravel,
      changeLog: appendTravelChangeLog(preparedTravel.changeLog ?? existing?.changeLog, change ? travelChangeEntry(change) : null),
    }, existing || {}, state);
    return {
      ...state,
      employeePortalTravels: existing
        ? safeArray(state.employeePortalTravels).map((entry) => entry.id === savedTravel.id ? savedTravel : entry)
        : [savedTravel, ...safeArray(state.employeePortalTravels)],
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mapPortalTravelToUi(savedTravel);
};

const deleteEmployeePortalTravel = async (travelId) => {
  if (!travelId) throw new Error(t('svc.missingTravel'));
  let deleted = false;
  await updateStateAndPersist((state) => {
    const existing = safeArray(state.employeePortalTravels).find((entry) => entry.id === travelId);
    if (!existing) throw new Error(t('svc.travelNotFound'));
    if (!['draft', 'submitted'].includes(String(existing.status || '').toLowerCase())) throw new Error(t('svc.onlyDraftsAndSubmitted'));
    deleted = true;
    return {
      ...state,
      employeePortalTravels: safeArray(state.employeePortalTravels).filter((entry) => entry.id !== travelId),
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return deleted;
};
